/**
 * TOSS Web Application - Card Component
 *
 * Card component for displaying content in a contained, styled box.
 * Supports header, body, footer sections with optional hover effects.
 *
 * Features:
 * - Header with title and actions
 * - Body content area
 * - Footer for actions
 * - Hover effect for interactive cards
 * - Multiple padding options
 * - Dividers between sections
 *
 * Uses design tokens from index.css for consistent styling:
 * - --color-bg-card: Card background color
 * - --color-border: Border color
 * - --color-bg-secondary: Footer background
 * - --color-text-primary: Primary text color
 * - --color-text-secondary: Secondary text color
 * - --radius-lg: Card border radius
 */

import React, { forwardRef, type HTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export type CardComponent = 'div' | 'button' | 'article';

type CardBaseProps = {
  /** Content to render inside the card */
  children: ReactNode;
  /** Enable hover effect for interactive cards */
  hoverable?: boolean;
  /** Padding size for body content */
  padding?: CardPadding;
  /** Make card a clickable element */
  as?: CardComponent;
  /** Additional class names */
  className?: string;
};

export type CardProps = CardBaseProps & (
  | (Omit<HTMLAttributes<HTMLDivElement>, 'children'> & { as?: 'div' | 'article' })
  | (Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & { as: 'button' })
);

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Header title */
  title?: ReactNode;
  /** Subtitle or description */
  subtitle?: ReactNode;
  /** Actions to show on the right side */
  actions?: ReactNode;
  /** Action to show on the right side (alias for actions) */
  action?: ReactNode;
  /** Additional class names */
  className?: string;
  /** Children to render in header */
  children?: ReactNode;
}

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  /** Content padding */
  padding?: CardPadding;
  /** Additional class names */
  className?: string;
  /** Children to render in body */
  children: ReactNode;
}

export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  /** Justify content direction */
  justify?: 'start' | 'end' | 'center' | 'between';
  /** Additional class names */
  className?: string;
  /** Children to render in footer */
  children: ReactNode;
}

// ============================================================================
// Styles
// ============================================================================

const paddingStyles: Record<CardPadding, string> = {
  none: 'p-0',
  sm: 'p-3 lg:p-4',
  md: 'p-4 lg:p-5',
  lg: 'p-5 lg:p-6',
};

const justifyStyles: Record<string, string> = {
  start: 'justify-start',
  end: 'justify-end',
  center: 'justify-center',
  between: 'justify-between',
};

// ============================================================================
// Card Component
// ============================================================================

/**
 * Card container component
 *
 * @example
 * ```tsx
 * // Basic card
 * <Card>
 *   <CardHeader title="Card Title" />
 *   <CardBody>Content goes here</CardBody>
 * </Card>
 *
 * // Interactive card
 * <Card hoverable onClick={handleClick}>
 *   <CardBody>Click me!</CardBody>
 * </Card>
 *
 * // Full card with all sections
 * <Card>
 *   <CardHeader
 *     title="Settings"
 *     subtitle="Manage your preferences"
 *     actions={<Button size="sm">Edit</Button>}
 *   />
 *   <CardBody>
 *     <SettingsForm />
 *   </CardBody>
 *   <CardFooter>
 *     <Button>Save Changes</Button>
 *   </CardFooter>
 * </Card>
 * ```
 */
export const Card = forwardRef<HTMLDivElement | HTMLButtonElement, CardProps>(
  (
    {
      children,
      hoverable = false,
      padding = 'none',
      as: Component = 'div',
      className = '',
      ...props
    },
    ref
  ) => {
    // Вид карточки не написан здесь и не пишется утилитами: `app-surface` в `index.css` —
    // единственное определение поверхности на продукт (белый фон, радиус 12, контур
    // `--color-border`, без тени). Ховер меняет цвет той же рамки, поэтому содержимое не
    // сдвигается; подъёма нет с 26.08.2026 — карточку отделяет канва.
    const cardClasses = [
      'app-surface',
      hoverable ? 'app-surface-interactive cursor-pointer' : '',
      padding !== 'none' ? paddingStyles[padding] : '',
      className,
    ]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    return (
      <Component
        ref={ref as React.Ref<HTMLDivElement & HTMLButtonElement>}
        className={cardClasses}
        {...(props as React.HTMLAttributes<HTMLElement>)}
      >
        {children}
      </Component>
    );
  }
);

Card.displayName = 'Card';

// ============================================================================
// Card Header
// ============================================================================

/**
 * Card header component with title, subtitle, and actions
 *
 * @example
 * ```tsx
 * <CardHeader
 *   title="User Profile"
 *   subtitle="View and edit your profile"
 *   actions={
 *     <Button variant="ghost" size="sm">
 *       Edit
 *     </Button>
 *   }
 * />
 * ```
 */
export function CardHeader({
  title,
  subtitle,
  actions,
  action,
  className = '',
  children,
  ...props
}: CardHeaderProps): JSX.Element {
  // Линия ВНУТРИ поверхности — волосок `--color-border-light`. Контур карточки рисует
  // `.app-surface`, и второй линией того же веса шапка спорила бы с ним (03.09.2026).
  const headerClasses = [
    'px-4 py-3 lg:px-5 lg:py-4 xl:px-6',
    'border-b border-[var(--color-border-light)]',
    className,
  ].join(' ');

  // Support both actions and action (alias)
  const headerActions = actions ?? action;

  // If children provided, render them directly
  if (children) {
    return (
      <div className={headerClasses} {...props}>
        {children}
      </div>
    );
  }

  return (
    <div className={headerClasses} {...props}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {title && (
            <h3 className="text-base font-medium text-[var(--color-text-primary)] truncate">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{subtitle}</p>
          )}
        </div>
        {headerActions && <div className="flex-shrink-0">{headerActions}</div>}
      </div>
    </div>
  );
}

// ============================================================================
// Card Body
// ============================================================================

/**
 * Card body component for main content
 *
 * @example
 * ```tsx
 * <CardBody padding="lg">
 *   <p>Card content goes here</p>
 * </CardBody>
 * ```
 */
export function CardBody({
  children,
  padding = 'md',
  className = '',
  ...props
}: CardBodyProps): JSX.Element {
  const bodyClasses = [
    padding === 'none' ? '' : 'px-4 py-4 lg:px-5 xl:px-6',
    padding === 'sm' && 'py-3',
    padding === 'lg' && 'py-6',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={bodyClasses} {...props}>
      {children}
    </div>
  );
}

// ============================================================================
// Card Footer
// ============================================================================

/**
 * Card footer component for actions
 *
 * @example
 * ```tsx
 * <CardFooter justify="end">
 *   <Button variant="secondary">Cancel</Button>
 *   <Button>Save</Button>
 * </CardFooter>
 * ```
 */
export function CardFooter({
  children,
  justify = 'end',
  className = '',
  ...props
}: CardFooterProps): JSX.Element {
  const footerClasses = [
    'flex items-center gap-3',
    'px-4 py-3 lg:px-5 lg:py-4 xl:px-6',
    'border-t border-[var(--color-border-light)]',
    'bg-[var(--color-bg-card)] rounded-b-[var(--radius-lg)]',
    justifyStyles[justify],
    className,
  ].join(' ');

  return (
    <div className={footerClasses} {...props}>
      {children}
    </div>
  );
}

// ============================================================================
// Simple Card Variants
// ============================================================================

export interface SimpleCardProps {
  /** Card title */
  title?: ReactNode;
  /** Card content */
  children: ReactNode;
  /** Icon to display */
  icon?: ReactNode;
  /** Additional class names */
  className?: string;
}

/**
 * Simple card with icon and title
 *
 * @example
 * ```tsx
 * <SimpleCard
 *   title="Total Users"
 *   icon={<UsersIcon />}
 * >
 *   <span className="text-2xl font-semibold">1,234</span>
 * </SimpleCard>
 * ```
 */
export function SimpleCard({
  title,
  children,
  icon,
  className = '',
}: SimpleCardProps): JSX.Element {
  return (
    <Card padding="md" className={className}>
      <div className="flex items-start gap-4">
        {icon && (
          <div className="flex-shrink-0 p-2 bg-primary-50 text-primary-600 rounded-[var(--radius-lg)]">
            {icon}
          </div>
        )}
        <div className="flex-1 min-w-0">
          {title && (
            <p className="text-sm font-medium text-[var(--color-text-secondary)] mb-1">{title}</p>
          )}
          <div>{children}</div>
        </div>
      </div>
    </Card>
  );
}

// ============================================================================
// Stat Card
// ============================================================================

export interface StatCardProps {
  /** Stat label */
  label: string;
  /** Stat value */
  value: ReactNode;
  /** Change indicator */
  change?: {
    value: number;
    type: 'increase' | 'decrease';
  };
  /** Icon to display */
  icon?: ReactNode;
  /** Additional class names */
  className?: string;
}

/**
 * Statistics card for dashboards
 *
 * @example
 * ```tsx
 * <StatCard
 *   label="Total Applications"
 *   value="1,234"
 *   change={{ value: 12, type: 'increase' }}
 *   icon={<DocumentIcon />}
 * />
 * ```
 */
export function StatCard({
  label,
  value,
  change,
  icon,
  className = '',
}: StatCardProps): JSX.Element {
  return (
    <Card padding="md" className={className}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-[var(--color-text-secondary)]">{label}</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-text-primary)]">{value}</p>
          {change && (
            <p
              className={`mt-1 text-sm flex items-center gap-1 ${
                change.type === 'increase' ? 'text-[var(--color-success)]' : 'text-[var(--color-error)]'
              }`}
            >
              {change.type === 'increase' ? (
                <ArrowUp className="w-4 h-4" />
              ) : (
                <ArrowDown className="w-4 h-4" />
              )}
              {change.value}%
            </p>
          )}
        </div>
        {icon && (
          <div className="p-3 bg-primary-50 text-primary-600 rounded-[var(--radius-lg)]">
            {icon}
          </div>
        )}
      </div>
    </Card>
  );
}

// ============================================================================
// Empty State Card
// ============================================================================

export interface EmptyStateCardProps {
  /** Empty state title */
  title: string;
  /** Empty state description */
  description?: string;
  /** Subtitle for filter context (e.g. "Попробуйте изменить параметры фильтра") */
  subtitle?: string;
  /** Icon to display */
  icon?: ReactNode;
  /** Action button */
  action?: ReactNode;
  /** Additional class names */
  className?: string;
}

/**
 * Empty state card for lists and tables
 *
 * @example
 * ```tsx
 * <EmptyStateCard
 *   title="No applications"
 *   description="Create your first application to get started"
 *   subtitle="Попробуйте изменить параметры фильтра"
 *   icon={<DocumentIcon />}
 *   action={<Button>Create Application</Button>}
 * />
 * ```
 */
export function EmptyStateCard({
  title,
  description,
  subtitle,
  icon,
  action,
  className = '',
}: EmptyStateCardProps): JSX.Element {
  return (
    <Card padding="lg" className={`text-center ${className}`}>
      {icon && (
        <div className="mx-auto w-16 h-16 text-[var(--color-text-disabled)] mb-4">{icon}</div>
      )}
      <h3 className="text-sm font-medium text-[var(--color-text-primary)]">{title}</h3>
      {description && (
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{description}</p>
      )}
      {subtitle && (
        <p className="mt-1 text-xs text-[var(--color-text-disabled)]">{subtitle}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </Card>
  );
}

export default Card;
